/**
 * ContentItem + ContentVersion request schemas — shared between the API
 * (server-side request validation) and the web SPA (client-side form
 * validation).
 *
 * Spec §6 ContentItem, §19 ContentItem, §19 ContentVersion, §20 schemaVersion:
 *   - A `ContentItem` is the durable identity of a piece of content. It owns
 *     no payload — the editable shape lives on its versions. The same
 *     `ContentItem` can be referenced by zero or more pages via
 *     `PageVersion.blocksOrderJson` (spec §19 explicitly forbids cascading a
 *     page tombstone down to its items, so they outlive their parent page).
 *   - `ContentVersion` is the versioned payload. `versionNumber` increments
 *     per ContentItem (`@@unique([contentItemId, versionNumber])`). The
 *     "currently published" version is enforced by the partial unique index
 *     `content_version_one_published_per_item` on
 *     `(contentItemId) WHERE status = 'PUBLISHED'`.
 *   - `contentJson`, `styleJson`, `aiMetaJson` all carry a top-level
 *     `schemaVersion: 1` per spec §20. The shared schemas in `./json.ts`
 *     pin `schemaVersion` to `z.literal(1)`, so unknown future versions are
 *     rejected by the validator before we ever touch the database.
 *
 * Type coupling rules (spec §19):
 *   - `type: PAGE_BLOCK` MUST carry a `blockType`. The block renderer keys
 *     off `blockType` to choose its template; a `PAGE_BLOCK` with no
 *     `blockType` would silently render as nothing. We enforce the pairing
 *     at the validator boundary so a typo can't slip through.
 *   - For non-`PAGE_BLOCK` types, `blockType` MUST be omitted. A `BLOG_POST`
 *     with a `HERO` blockType is structurally meaningless and would corrupt
 *     analytics aggregations downstream.
 *
 * Slice boundary:
 *   - This file owns the request shapes for the CRUD endpoints (issue #11).
 *   - Publish/unpublish/rollback shapes live in their own slice files
 *     (issues #12, #13).
 *   - Flag shapes live in a separate file (issue #14).
 */

import { z } from "zod";
import type { BlockType, ContentItemType, ContentStatus } from "../types/index.js";
import type { AiJudgeResultResponse } from "./judges.js";
import type { ReferencingExperimentRef } from "./pages.js";
import {
  type AiMetaJsonV1,
  type ContentJsonV1,
  type StyleJsonV1,
  aiMetaJsonV1,
  contentJsonV1,
  styleJsonV1,
} from "./json.js";

/**
 * Mirror of `ContentItemType` in `shared/src/types/index.ts`. Centralised
 * here so Zod can enumerate the literal values at runtime.
 */
export const contentItemTypeSchema = z.enum([
  "PAGE_BLOCK",
  "BLOG_POST",
  "PRODUCT",
  "FAQ",
  "CTA",
  "IMAGE",
  "SEO",
  "CUSTOM",
]) satisfies z.ZodType<ContentItemType>;

/**
 * Mirror of `BlockType` in `shared/src/types/index.ts`. Only valid when
 * `type === 'PAGE_BLOCK'`; the cross-field rule below enforces that pairing.
 */
export const blockTypeSchema = z.enum([
  "HERO",
  "TEXT",
  "FEATURE_GRID",
  "BENEFITS",
  "PROBLEM_SOLUTION",
  "CTA",
  "FAQ",
  "TESTIMONIALS",
  "PRODUCT_GRID",
  "BLOG_CONTENT",
  "BLOG_LIST",
  "CONTACT_FORM",
  "IMAGE",
  "COMPARISON_TABLE",
  "PRICING_TABLE",
  "TRUST_BADGES",
  "NEWSLETTER",
]) satisfies z.ZodType<BlockType>;

/**
 * Body schema for `POST /api/admin/sites/:siteId/content-items`.
 *
 * `.strict()` rejects unknown top-level keys so a typo like `blockTpe`
 * surfaces as a 422 instead of being silently dropped. The `superRefine`
 * enforces the `type`/`blockType` pairing rule documented above.
 */
export const createContentItemSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required").max(200),
    type: contentItemTypeSchema,
    blockType: blockTypeSchema.optional(),
    pageId: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.type === "PAGE_BLOCK" && value.blockType === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["blockType"],
        message: "blockType is required when type is PAGE_BLOCK",
      });
    }
    if (value.type !== "PAGE_BLOCK" && value.blockType !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["blockType"],
        message: "blockType is only allowed when type is PAGE_BLOCK",
      });
    }
  });
export type CreateContentItemInput = z.infer<typeof createContentItemSchema>;

/**
 * Query-parameter schema for `GET /api/admin/sites/:siteId/content-items`.
 * Standard offset pagination per `standards/api-patterns.md` (default 20,
 * max 100).
 */
export const listContentItemsQuerySchema = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict();
export type ListContentItemsQuery = z.infer<typeof listContentItemsQuerySchema>;

/**
 * Body schema for `POST /api/admin/content-items/:id/versions`.
 *
 * `contentJson` is required (a version with no payload is meaningless).
 * `styleJson` and `aiMetaJson` are optional. All three are pinned to
 * `schemaVersion: 1` via the shared schemas in `./json.ts`; an unknown future
 * version is rejected by Zod before the row is written, per spec §20.
 *
 * `.strict()` rejects unknown top-level keys; field-level passthrough on
 * `contentJsonV1` is intentional — block payload shapes vary by `blockType`
 * and the per-block validators live in slice 2.
 */
export const createContentVersionSchema = z
  .object({
    contentJson: contentJsonV1,
    styleJson: styleJsonV1.optional(),
    aiMetaJson: aiMetaJsonV1.optional(),
  })
  .strict();
export type CreateContentVersionInput = z.infer<typeof createContentVersionSchema>;

/**
 * Body schema for `PATCH /api/admin/content-versions/:id`. All three JSON
 * fields are independently optional so an editor can patch only the parts
 * it cares about. The status is NOT mutable here — the publish/unpublish
 * workflow lives in slice 2 (#12, #13). The service enforces a draft-only
 * guard at the row level: any non-DRAFT version is rejected with 422 before
 * the patch runs.
 */
export const updateContentVersionSchema = z
  .object({
    contentJson: contentJsonV1.optional(),
    styleJson: styleJsonV1.optional(),
    aiMetaJson: aiMetaJsonV1.optional(),
  })
  .strict()
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "Update must include at least one field",
  });
export type UpdateContentVersionInput = z.infer<typeof updateContentVersionSchema>;

/**
 * Public-facing ContentItem shape. ISO-string timestamps for transport.
 * `deletedAt` never leaves the service layer — the response is only emitted
 * for active rows.
 */
export type ContentItemResponse = {
  id: string;
  siteId: string;
  pageId: string | null;
  type: ContentItemType;
  blockType: BlockType | null;
  name: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * Public-facing ContentVersion shape. ISO-string timestamps for transport,
 * JSON fields untyped at the boundary (clients narrow with the shared
 * `contentJsonV1` etc. schemas if needed). `createdFromVersionId`,
 * `publishedAt`, `archivedAt` are slice-2 concerns; we return them as null
 * for slice-1 drafts but keep the field present so the wire shape doesn't
 * change between slices.
 *
 * `judges` (issue #217): the five AI judge results that fired against this
 * version on AI Studio Accept. `null` means no judge run has landed (manual
 * / pre-AI version). An empty array is the defensive "judges ran but
 * produced zero rows" sentinel; in practice the runner always writes five
 * rows on success, so the SPA can render an empty array the same as null
 * (no chips).
 */
export type ContentVersionResponse = {
  id: string;
  contentItemId: string;
  versionNumber: number;
  status: ContentStatus;
  contentJson: unknown;
  styleJson: unknown;
  aiMetaJson: unknown;
  createdFromVersionId: string | null;
  createdBy: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  judges: AiJudgeResultResponse[] | null;
  /**
   * Issue #255 AC1 — experiments whose variants reference this
   * ContentVersion via `contentOverridesJson.overrides[]`. Populated only
   * for EXPERIMENT-status rows inside `experimentVersions[]`; other slots
   * leave the field `undefined`. The SPA reads `?? []` defensively so the
   * field's absence renders the same as an empty list.
   */
  referencingExperiments?: ReferencingExperimentRef[];
};

/**
 * Composite shape returned by `GET /api/admin/content-items/:id`. Mirrors the
 * `PageWithVersionsResponse` pattern from the Pages slice: latest draft
 * (newest by `versionNumber`), currently-published version (resolved via
 * the partial unique index on `(contentItemId, status='PUBLISHED')`), and
 * the item's archived versions ordered newest-archived-first.
 *
 * `archived` is what the admin per-version surface (issue #173) binds the
 * rollback + per-version Preview affordances off, mirroring #186's
 * page-detail dead-end recovery shape. On a freshly-created item the array
 * is empty.
 */
export type ContentItemWithVersionsResponse = {
  item: ContentItemResponse;
  latestDraft: ContentVersionResponse | null;
  published: ContentVersionResponse | null;
  /**
   * EXPERIMENT-status versions (issue #243). Newest-first. Multiple may
   * coexist by design (the partial unique index that guards "one
   * PUBLISHED per content item" deliberately does NOT cover EXPERIMENT).
   * Each row is a candidate target for the experiment-variant
   * `contentOverridesJson` editor.
   */
  experimentVersions: ContentVersionResponse[];
  archived: ContentVersionResponse[];
};

// Re-export the JSON shapes the API and SPA both import.
export type { AiMetaJsonV1, ContentJsonV1, StyleJsonV1 };
